import React from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';

const ActualiteFormationGenreGitegaPage = () => {
  // Cinq photos disponibles
  const photoDG = '/assets/genre/DG_INSBU.jpeg';
  const photoOnuFemmes = '/assets/genre/R_ONI_FEMME.jpeg';
  const photoAllocutionCgg = '/assets/genre/ALLOCUTION_CGG.jpeg';
  const photoPresentation = '/assets/genre/PRESENTATION.jpeg';
  const photoFamille = '/assets/genre/PHOTO_FAMMILLE.jpeg';

  // Photo de couverture (hero) : la photo de famille, la plus représentative
  const photoHero = photoFamille;

  return (
    <div style={styles.container}>
      <NavigationBar />

      <main style={styles.main}>
        <header style={styles.header}>
          {/* BADGES */}
          <div style={styles.badges}>
            <span style={styles.badgeRed}>INSBU</span>
            <span style={styles.badgeGreen}>ONU Femmes</span>
            <span style={styles.badgeGray}>Actualité</span>
          </div>

          {/* TITRE ACCROCHEUR */}
          <h1 style={styles.title}>
            À Gitega, l’INSBU et ONU Femmes outillent les producteurs de données sur les statistiques de genre
          </h1>

          {/* MÉTADONNÉES */}
          <div style={styles.meta}>
            <span style={styles.metaItem}>
              <CalendarTodayIcon style={styles.metaIcon} /> 05 – 09 octobre 2026
            </span>
            <span style={styles.metaItem}>
              <LocationOnIcon style={styles.metaIcon} /> Gitega, Burundi
            </span>
            <span style={styles.metaItem}>
              <AccessTimeIcon style={styles.metaIcon} /> Lecture 4 min
            </span>
          </div>

          {/* PHOTO DE COUVERTURE */}
          <div style={styles.imageWrapper}>
            <img
              src={photoHero}
              alt="Photo de famille de l'atelier sur les statistiques de genre à Gitega"
              style={styles.heroImage}
              onError={(e) => {
                e.target.src = 'https://placehold.co/820x420/008D36/FFFFFF?text=Atelier+Genre+Gitega+2026';
              }}
            />
            <p style={styles.imageCaption}>
              Photo de famille réunissant les autorités, les experts et les participants à l’issue de la cérémonie d’ouverture de l’atelier de Gitega.
            </p>
          </div>

          {/* CHAPEAU - RÉSUMÉ EN 5W */}
          <p style={styles.chapeau}>
            <strong>Gitega, 5 octobre 2026</strong> — L’<strong>Institut National de la Statistique du Burundi (INSBU)</strong>, en partenariat avec <strong>ONU Femmes</strong>, organise du <strong>5 au 9 octobre 2026 à Gitega</strong> un atelier de formation sur les <strong>statistiques de genre</strong>. La cérémonie d’ouverture a été rehaussée par la présence du Directeur Général de l’INSBU, <strong>M. Nicolas NDAYISHIMIYE</strong>, de la Représentante Résidente d’ONU Femmes au Burundi, <strong>Mme Clara ANYANGWE</strong>, ainsi que du Conseiller juridique du Gouverneur de la province de Gitega, <strong>M. Domitien NIMPAGARITSE</strong>.
          </p>
        </header>

        {/* CORPS DE L'ARTICLE */}
        <article style={styles.article}>

          {/* SECTION 1 : CONTEXTE ET OBJECTIF */}
          <h2 style={styles.h2}>
            <TrendingUpIcon style={{ fontSize: '1.8rem', marginRight: '10px', verticalAlign: 'middle', color: '#008D36' }} />
            Renforcer les capacités pour mieux intégrer le genre
          </h2>
          <p style={styles.p}>
            Cette formation vise à <strong>renforcer les capacités des producteurs et des utilisateurs de données statistiques relatives au genre</strong>, afin d’améliorer la prise en compte de la dimension genre dans l’élaboration, la mise en œuvre et le suivi des politiques et programmes de développement.
          </p>
          <p style={styles.p}>
            Elle réunit des cadres de l’INSBU, des représentants du <strong>ministère en charge de la Justice, des Droits de la Personne Humaine et du Genre</strong>, les <strong>points focaux genre des ministères sectoriels</strong>, ainsi que des représentants des institutions nationales, de la société civile et des agences des Nations Unies.
          </p>
          <p style={styles.p}>
            Cette initiative contribue à renforcer la <strong>disponibilité, la qualité et l’utilisation des statistiques sensibles au genre</strong> pour une prise de décision fondée sur des données probantes et le suivi des progrès en matière d’égalité entre les femmes et les hommes, en appui à la <strong>Vision Burundi 2040-2060</strong> et au <strong>PND révisé</strong>.
          </p>

          {/* SECTION 2 : OBJECTIFS DE LA FORMATION - BASÉE SUR L'AGENDA */}
          <h2 style={styles.h2}>
            <SchoolIcon style={{ fontSize: '1.8rem', marginRight: '10px', verticalAlign: 'middle', color: '#008D36' }} />
            Cinq jours pour passer de la donnée à la décision
          </h2>
          <p style={styles.p}>
            Structuré autour de <strong>sept modules progressifs</strong>, l’atelier alterne apports théoriques, échanges entre producteurs et utilisateurs de données, et travaux pratiques par ministère. L’objectif : transformer les données brutes en véritables outils d’aide à la décision.
          </p>

          {/* BLOC 1 : MODULES FONDAMENTAUX */}
          <h3 style={styles.h3}>1. Les fondamentaux des statistiques de genre</h3>
          <div style={styles.modulesGrid}>
            <div style={styles.moduleCard}>
              <span style={styles.moduleNumber}>01</span>
              <h4 style={styles.moduleTitle}>Qu’est-ce qu’une donnée de genre ?</h4>
              <p style={styles.moduleText}>
                Comprendre la définition d’une donnée de genre et son utilisation pour le suivi des <strong>Objectifs de Développement Durable (ODD)</strong>.
              </p>
            </div>
            <div style={styles.moduleCard}>
              <span style={styles.moduleNumber}>02</span>
              <h4 style={styles.moduleTitle}>Connaître les données sur le genre</h4>
              <p style={styles.moduleText}>
                Identifier les sources existantes et <strong>éviter les erreurs courantes</strong> d’interprétation ou de comparaison.
              </p>
            </div>
            <div style={styles.moduleCard}>
              <span style={styles.moduleNumber}>03</span>
              <h4 style={styles.moduleTitle}>Calculer les statistiques de genre</h4>
              <p style={styles.moduleText}>
                Maîtriser les méthodes de calcul et de construction des <strong>indicateurs désagrégés par sexe</strong> pour le suivi des ODD.
              </p>
            </div>
          </div>

          {/* BLOC 2 : PRODUCTION ET DIALOGUE */}
          <h3 style={styles.h3}>2. Production, dialogue et communication</h3>
          <div style={styles.modulesGrid}>
            <div style={styles.moduleCard}>
              <span style={styles.moduleNumber}>04</span>
              <h4 style={styles.moduleTitle}>Dialogue producteurs – utilisateurs</h4>
              <p style={styles.moduleText}>
                Créer un espace d’échange entre ceux qui produisent les données et ceux qui les utilisent pour <strong>aligner l’offre et la demande</strong> statistique.
              </p>
            </div>
            <div style={styles.moduleCard}>
              <span style={styles.moduleNumber}>05</span>
              <h4 style={styles.moduleTitle}>Méthodes de collecte et d’estimation</h4>
              <p style={styles.moduleText}>
                Panorama des techniques de collecte, d’échantillonnage et d’estimation appliquées aux <strong>enquêtes sensibles au genre</strong>.
              </p>
            </div>
            <div style={styles.moduleCard}>
              <span style={styles.moduleNumber}>06</span>
              <h4 style={styles.moduleTitle}>Conception des outils de collecte</h4>
              <p style={styles.moduleText}>
                Élaborer des questionnaires, des nomenclatures et des supports adaptés à la <strong>ventilation par sexe</strong> des données.
              </p>
            </div>
            <div style={styles.moduleCard}>
              <span style={styles.moduleNumber}>07</span>
              <h4 style={styles.moduleTitle}>Communiquer les données de genre</h4>
              <p style={styles.moduleText}>
                Rédiger des rapports analytiques percutants et utiliser les <strong>outils modernes de visualisation</strong> pour le plaidoyer.
              </p>
            </div>
          </div>

          {/* BLOC 3 : TRAVAUX PRATIQUES */}
          <h3 style={styles.h3}>3. Travaux pratiques et mise en situation</h3>
          <div style={styles.practicalBlock}>
            <div style={styles.practicalItem}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '10px', flexShrink: 0 }} />
              <div>
                <strong>Aperçu du développement des statistiques de genre au Burundi</strong>
                <p style={styles.practicalText}>État des lieux et trajectoire nationale en matière de production de données désagrégées.</p>
              </div>
            </div>
            <div style={styles.practicalItem}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '10px', flexShrink: 0 }} />
              <div>
                <strong>Ensemble minimal d’indicateurs relatifs au genre</strong>
                <p style={styles.practicalText}>Sélection d’un noyau d’indicateurs prioritaires pour le suivi national et régional.</p>
              </div>
            </div>
            <div style={styles.practicalItem}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '10px', flexShrink: 0 }} />
              <div>
                <strong>Fiche de statistiques clés par ministère</strong>
                <p style={styles.practicalText}>Travail en groupes sectoriels : indicateurs, périodicité et méthodologie de collecte.</p>
              </div>
            </div>
            <div style={styles.practicalItem}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '10px', flexShrink: 0 }} />
              <div>
                <strong>Discussions sur les lacunes des statistiques de genre</strong>
                <p style={styles.practicalText}>Identification collective des gaps et propositions d’actions concrètes pour le SSN.</p>
              </div>
            </div>
          </div>

          {/* ENCADRÉ CITATION */}
          <div style={styles.quoteBlock}>
            <ChatBubbleOutlineIcon style={{ fontSize: '2.5rem', color: '#008D36', marginBottom: '10px' }} />
            <blockquote style={styles.quoteText}>
              « Cette initiative contribue à renforcer la disponibilité, la qualité et l’utilisation des statistiques sensibles au genre pour une prise de décision fondée sur des données probantes et le suivi des progrès en matière d’égalité entre les femmes et les hommes. »
            </blockquote>
            <p style={styles.quoteAuthor}>— Objectif de l’atelier INSBU – ONU Femmes, Gitega 2026</p>
          </div>

          {/* SECTION 3 : RÉSULTATS ATTENDUS */}
          <h2 style={styles.h2}>
            <CheckCircleIcon style={{ fontSize: '1.8rem', marginRight: '10px', verticalAlign: 'middle', color: '#008D36' }} />
            Des retombées concrètes pour le Système Statistique National
          </h2>
          <p style={styles.p}>
            En réunissant producteurs et utilisateurs de données autour d’une même table, l’INSBU et ONU Femmes entendent consolider l’ensemble de la chaîne de production statistique. Les résultats attendus à l’issue de la session sont :
          </p>
          <div style={styles.gridOutcomes}>
            <div style={styles.outcomeCard}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '8px' }} />
              <span>Une meilleure disponibilité des données désagrégées par sexe.</span>
            </div>
            <div style={styles.outcomeCard}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '8px' }} />
              <span>Une qualité accrue des rapports analytiques sectoriels.</span>
            </div>
            <div style={styles.outcomeCard}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '8px' }} />
              <span>Une intégration renforcée du genre dans les politiques et programmes.</span>
            </div>
            <div style={styles.outcomeCard}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '8px' }} />
              <span>Un dialogue consolidé entre producteurs et utilisateurs de statistiques.</span>
            </div>
          </div>

          {/* GALERIE PHOTOS (4 images) */}
          <div style={styles.gallery}>
            <h2 style={styles.h2Gallery}>
              <PhotoCameraIcon style={{ fontSize: '1.8rem', marginRight: '10px', verticalAlign: 'middle', color: '#008D36' }} />
              Galerie : Les temps forts de l’ouverture
            </h2>
            <div style={styles.galleryGrid}>
              <div style={styles.galleryItem}>
                <img
                  src={photoDG}
                  alt="Le DG de l'INSBU lors de son allocution"
                  style={styles.galleryImg}
                  onError={(e) => e.target.src = 'https://placehold.co/300x200/008D36/FFFFFF?text=DG+INSBU'}
                />
                <p style={styles.galleryCaption}>Le Directeur Général de l’INSBU, M. Nicolas NDAYISHIMIYE, lors de son allocution.</p>
              </div>
              <div style={styles.galleryItem}>
                <img
                  src={photoOnuFemmes}
                  alt="La Représentante Résidente d'ONU Femmes lors de son allocution"
                  style={styles.galleryImg}
                  onError={(e) => e.target.src = 'https://placehold.co/300x200/008D36/FFFFFF?text=ONU+Femmes'}
                />
                <p style={styles.galleryCaption}>La Représentante Résidente d’ONU Femmes au Burundi, Mme Clara ANYANGWE, lors de son allocution.</p>
              </div>
              <div style={styles.galleryItem}>
                <img
                  src={photoAllocutionCgg}
                  alt="Le Conseiller juridique du Gouverneur de Gitega, la Représentante d'ONU Femmes et le DG de l'INSBU"
                  style={styles.galleryImg}
                  onError={(e) => e.target.src = 'https://placehold.co/300x200/008D36/FFFFFF?text=Allocution+officielle'}
                />
                <p style={styles.galleryCaption}>Le Conseiller juridique du Gouverneur de Gitega, M. Domitien NIMPAGARITSE, aux côtés de la Représentante d’ONU Femmes et du DG de l’INSBU.</p>
              </div>
              <div style={styles.galleryItem}>
                <img
                  src={photoPresentation}
                  alt="Première présentation de l'atelier"
                  style={styles.galleryImg}
                  onError={(e) => e.target.src = 'https://placehold.co/300x200/008D36/FFFFFF?text=Presentation'}
                />
                <p style={styles.galleryCaption}>Les participants attentifs lors de la première présentation de l’atelier.</p>
              </div>
            </div>
          </div>

          {/* CONCLUSION ET REMERCIEMENTS */}
          <div style={styles.conclusion}>
            <h2 style={styles.h2}>
              <PeopleIcon style={{ fontSize: '1.8rem', marginRight: '10px', verticalAlign: 'middle', color: '#008D36' }} />
              Vers une culture statistique sensible au genre
            </h2>
            <p style={styles.p}>
              À travers ce partenariat, l’INSBU et ONU Femmes réaffirment leur engagement commun en faveur d’un système statistique national capable de produire des données fiables, comparables et désagrégées, au service de l’égalité entre les femmes et les hommes.
            </p>
            <p style={styles.p}>
              Les compétences acquises durant ces cinq jours devraient permettre aux participants de mieux éclairer les décisions publiques et de suivre avec davantage de précision les progrès accomplis, en cohérence avec la <strong>Vision Burundi 2040-2060</strong> et le <strong>Plan National de Développement révisé</strong>.
            </p>
            <div style={styles.vive}>
              <p><strong>Vive la coopération entre l’INSBU et ONU Femmes !</strong></p>
              <p><strong>Vive les statistiques de genre au service du développement !</strong></p>
            </div>
          </div>

        </article>
      </main>

      <Footer />
    </div>
  );
};

// ================= STYLES =================
const styles = {
  container: {
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  main: {
    flex: 1,
    maxWidth: '820px',
    margin: '0 auto',
    padding: '40px 20px',
    width: '100%',
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
    paddingBottom: '30px',
    borderBottom: '2px solid #e0e0e0',
  },
  badges: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  badgeRed: {
    backgroundColor: '#E30613',
    color: 'white',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  badgeGreen: {
    backgroundColor: '#008D36',
    color: 'white',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  badgeGray: {
    backgroundColor: '#6c757d',
    color: 'white',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  title: {
    color: '#1a1a1a',
    fontSize: '2.4rem',
    fontWeight: '700',
    lineHeight: 1.2,
    marginBottom: '12px',
    letterSpacing: '-0.3px',
  },
  meta: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '20px',
    marginBottom: '20px',
    fontSize: '0.9rem',
    color: '#6c757d',
  },
  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  },
  metaIcon: {
    fontSize: '1rem',
    color: '#008D36',
  },
  imageWrapper: {
    marginBottom: '24px',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  heroImage: {
    width: '100%',
    height: 'auto',
    maxHeight: '420px',
    objectFit: 'cover',
    display: 'block',
    borderRadius: '8px 8px 0 0',
  },
  imageCaption: {
    fontSize: '0.85rem',
    color: '#6c757d',
    textAlign: 'center',
    padding: '8px 0 4px 0',
    fontStyle: 'italic',
    backgroundColor: '#fafafa',
    margin: 0,
    borderBottom: '1px solid #eaeaea',
    borderRadius: '0 0 8px 8px',
  },
  chapeau: {
    fontSize: '1.2rem',
    lineHeight: 1.7,
    color: '#2c3e50',
    maxWidth: '700px',
    margin: '0 auto',
    fontWeight: '400',
    borderLeft: '4px solid #008D36',
    paddingLeft: '20px',
    textAlign: 'left',
  },
  article: {
    backgroundColor: 'white',
    padding: '40px 45px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
    marginBottom: '40px',
  },
  h2: {
    color: '#008D36',
    fontSize: '1.5rem',
    fontWeight: '600',
    marginTop: '40px',
    marginBottom: '16px',
    paddingBottom: '6px',
    borderBottom: '2px solid #eaeaea',
    display: 'flex',
    alignItems: 'center',
  },
  h2Gallery: {
    color: '#008D36',
    fontSize: '1.5rem',
    fontWeight: '600',
    marginTop: '20px',
    marginBottom: '16px',
    paddingBottom: '6px',
    borderBottom: '2px solid #eaeaea',
    display: 'flex',
    alignItems: 'center',
  },
  // ===== SECTION OBJECTIFS / MODULES =====
  h3: {
    color: '#1a3b2e',
    fontSize: '1.15rem',
    fontWeight: '700',
    marginTop: '30px',
    marginBottom: '15px',
    paddingLeft: '12px',
    borderLeft: '4px solid #008D36',
  },
  modulesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '15px',
    marginBottom: '10px',
  },
  moduleCard: {
    position: 'relative',
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '20px 18px 16px 18px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  moduleNumber: {
    position: 'absolute',
    top: '-10px',
    left: '18px',
    backgroundColor: '#008D36',
    color: 'white',
    fontSize: '0.75rem',
    fontWeight: '700',
    padding: '3px 10px',
    borderRadius: '20px',
    letterSpacing: '0.5px',
  },
  moduleTitle: {
    color: '#008D36',
    fontSize: '1rem',
    fontWeight: '700',
    margin: '8px 0 8px 0',
    lineHeight: 1.3,
  },
  moduleText: {
    fontSize: '0.9rem',
    lineHeight: 1.6,
    color: '#444',
    margin: 0,
    textAlign: 'justify',
  },
  practicalBlock: {
    backgroundColor: '#f0f7f2',
    border: '1px solid #d0e6d6',
    borderRadius: '10px',
    padding: '20px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  practicalItem: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: '0.95rem',
    color: '#1e1e1e',
    lineHeight: 1.5,
  },
  practicalText: {
    fontSize: '0.85rem',
    color: '#555',
    margin: '4px 0 0 0',
    lineHeight: 1.5,
  },
  p: {
    fontSize: '1.05rem',
    lineHeight: 1.8,
    color: '#1e1e1e',
    marginBottom: '20px',
    textAlign: 'justify',
  },
  ul: {
    paddingLeft: '20px',
    marginBottom: '20px',
  },
  li: {
    fontSize: '1.05rem',
    lineHeight: 1.8,
    color: '#1e1e1e',
    marginBottom: '8px',
  },
  // BLOC CITATION
  quoteBlock: {
    backgroundColor: '#f0f7f2',
    padding: '25px 30px',
    borderRadius: '12px',
    margin: '30px 0',
    textAlign: 'center',
    border: '1px solid #d0e6d6',
  },
  quoteText: {
    fontSize: '1.4rem',
    lineHeight: 1.6,
    color: '#1a3b2e',
    fontWeight: '400',
    fontStyle: 'italic',
    margin: '0 0 10px 0',
  },
  quoteAuthor: {
    fontSize: '1rem',
    color: '#555',
    margin: 0,
    fontWeight: '600',
  },
  // GRILLE POUR LES RÉSULTATS
  gridOutcomes: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '20px',
  },
  outcomeCard: {
    display: 'flex',
    alignItems: 'flex-start',
    backgroundColor: '#f9fafb',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #eaeaea',
    fontSize: '0.95rem',
    lineHeight: 1.5,
    color: '#1e1e1e',
  },
  // GALERIE PHOTOS
  gallery: {
    marginTop: '30px',
  },
  galleryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '15px',
  },
  galleryItem: {
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  galleryImg: {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
    display: 'block',
  },
  galleryCaption: {
    fontSize: '0.8rem',
    color: '#555',
    padding: '6px 10px',
    textAlign: 'center',
    fontStyle: 'italic',
    margin: 0,
    borderTop: '1px solid #eaeaea',
    backgroundColor: '#ffffff',
  },
  conclusion: {
    marginTop: '40px',
    paddingTop: '20px',
    borderTop: '2px solid #eaeaea',
  },
  vive: {
    marginTop: '20px',
    textAlign: 'center',
    fontSize: '1.1rem',
    lineHeight: 2,
    color: '#008D36',
    fontWeight: '600',
  },
};

export default ActualiteFormationGenreGitegaPage;